import { computed, nextTick } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppMultiSelectSearchableProps } from '../types/interfaces'
import type { UseAppSelectCoreReturn } from '../../app-select'

import { useAppSelectCore, useSelectSearch } from '../../app-select'
import { createMultiSelectionAdapter, useSelectAll } from '../../app-multi-select'

interface UseAppMultiSelectSearchableOptions<TValue = string | number> {
  modelValue: Ref<TValue[] | undefined>
  props: AppMultiSelectSearchableProps<TValue>
  onOpen: () => void
  onClose: () => void
  onSearch: (query: string) => void
  onLoadMore: (query: string) => void
}

interface UseAppMultiSelectSearchableReturn<TValue = string | number>
  extends UseAppSelectCoreReturn<TValue> {
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
  inputValue: WritableComputedRef<string>
  inputPlaceholder: ComputedRef<string>
  selectedValues: ComputedRef<TValue[]>
  isOptionSelected: (option: AppSelectOption<TValue>) => boolean
  /** True when every enabled visible option is in the model (and there is at
   *  least one enabled visible option). */
  isAllSelected: ComputedRef<boolean>
  /** True when there are no enabled visible options to toggle. */
  isSelectAllDisabled: ComputedRef<boolean>
  /** Toggle all enabled visible options in the model. */
  toggleSelectAll: () => void
  showClearButton: ComputedRef<boolean>
  showNoResults: ComputedRef<boolean>
  isClearDisabled: ComputedRef<boolean>
  isLoadMoreDisabled: ComputedRef<boolean>
  handleInputFocus: () => void
  handleInputBlur: () => void
  handleClearClick: () => void
  handleLoadMoreClick: () => void
}

export const useAppMultiSelectSearchable = <TValue = string | number>(
  options: UseAppMultiSelectSearchableOptions<TValue>,
): UseAppMultiSelectSearchableReturn<TValue> => {
  const { modelValue, props, onOpen, onClose, onSearch, onLoadMore } = options

  // ── Shared search engine (query, displayedOptions, flags, debounce) ──
  const {
    query,
    isFocused,
    displayedOptions,
    showClearButton,
    showNoResults,
    isClearDisabled,
    isLoadMoreDisabled,
    handleInputBlur,
    handleLoadMoreClick,
    resetQuery,
  } = useSelectSearch<TValue>({ props, onSearch, onLoadMore })

  const selectedValues = computed<TValue[]>(() => modelValue.value ?? [])

  // Resolve selected option records for the trigger text — prefer props.options,
  // fall back to props.selectedOptions for off-page values (server mode).
  const resolvedSelectedOptions = computed<AppSelectOption<TValue>[]>(() => {
    const fromOptions = props.options.filter(o => selectedValues.value.includes(o.value))
    const missing = selectedValues.value.filter(
      v => !fromOptions.some(o => o.value === v),
    )
    if (missing.length === 0) return fromOptions
    const fallback = (props.selectedOptions ?? []).filter(o => missing.includes(o.value))
    return [...fromOptions, ...fallback]
  })

  const selection = createMultiSelectionAdapter<TValue>({ modelValue, selectedValues })
  // Reuse the adapter's predicate for the template / return surface.
  const isOptionSelected = selection.isSelected

  const { isAllSelected, isSelectAllDisabled, toggleSelectAll } = useSelectAll<TValue>({
    modelValue,
    selectedValues,
    displayedOptions,
  })

  const core = useAppSelectCore<TValue>({
    props,
    displayedOptions,
    mode: { inputEditable: true },
    selection,
    onOpen,
    onClose: () => {
      resetQuery()
      onClose()
    },
  })

  // ── Input bridge ────────────────────────────────────
  const inputValue: WritableComputedRef<string> = computed({
    get: () => {
      if (isFocused.value || query.value !== '') return query.value
      return resolvedSelectedOptions.value.map(o => o.text).join(', ')
    },
    set: (value: string) => {
      query.value = value
    },
  })

  const inputPlaceholder = computed<string>(() => {
    if (isFocused.value) return ''
    if (selectedValues.value.length > 0) return ''
    return props.placeholder ?? ''
  })

  // ── Core-coupled handlers (need core.inputRef, so they live here) ──
  const handleInputFocus = (): void => {
    isFocused.value = true
    core.handleInputFocus()
  }

  const handleClearClick = (): void => {
    if (isClearDisabled.value) return
    query.value = ''
    void nextTick(() => core.inputRef.value?.focus())
  }

  return {
    ...core,
    displayedOptions,
    inputValue,
    inputPlaceholder,
    selectedValues,
    isOptionSelected,
    isAllSelected,
    isSelectAllDisabled,
    toggleSelectAll,
    showClearButton,
    showNoResults,
    isClearDisabled,
    isLoadMoreDisabled,
    handleInputFocus,
    handleInputBlur,
    handleClearClick,
    handleLoadMoreClick,
  }
}
